import * as appConfig from '../config';
import { resolveKryspaceReadSource } from '../config/kryspaceReadSource';
import { apiClient } from './apiClient';
import {
  isArtifactDetailPayload,
  isArtifactItemDetailPayload,
  type ArtifactDetailPayload,
  type ArtifactItemDetailPayload,
} from '../types/kryspaceEntityDetail';
import {
  KryspaceRenderServiceError,
  mapRenderManifestHttpError,
} from './kryspaceRenderService';

function readConfig() {
  return {
    apiBaseUrl: appConfig.API_BASE_URL,
  };
}

function artifactDetailUrl(slug: string, artifactInstanceId: string): string {
  const source = resolveKryspaceReadSource({ workspaceSlug: slug });
  return `/kryspace/workspace/${encodeURIComponent(slug)}/artifact-detail/${encodeURIComponent(artifactInstanceId)}?source=${source}`;
}

function artifactItemDetailUrl(slug: string, itemId: string): string {
  const source = resolveKryspaceReadSource({ workspaceSlug: slug });
  return `/kryspace/workspace/${encodeURIComponent(slug)}/artifact-item-detail/${encodeURIComponent(itemId)}?source=${source}`;
}

async function fetchPayload<T>(
  url: string,
  validate: (value: unknown) => value is T
): Promise<T> {
  const { apiBaseUrl } = readConfig();
  if (!apiBaseUrl) {
    throw new KryspaceRenderServiceError(
      'Kryspace read API is not configured. Set REACT_APP_API_URL.',
      'NOT_CONFIGURED'
    );
  }

  try {
    const response = await apiClient.get<unknown>(url, {
      baseURL: apiBaseUrl.replace(/\/$/, ''),
    });
    if (!validate(response.data)) {
      throw new KryspaceRenderServiceError(
        'Entity detail response failed schema validation',
        'INVALID_RESPONSE'
      );
    }
    return response.data;
  } catch (err: unknown) {
    if (err instanceof KryspaceRenderServiceError) {
      throw err;
    }
    throw mapRenderManifestHttpError(err);
  }
}

export async function fetchArtifactDetail(
  workspaceSlug: string,
  artifactInstanceId: string
): Promise<ArtifactDetailPayload> {
  const slug = workspaceSlug.trim();
  const id = artifactInstanceId.trim();
  if (!slug || !id) {
    throw new KryspaceRenderServiceError('Workspace slug and artifact id are required', 'NOT_CONFIGURED');
  }
  return fetchPayload(artifactDetailUrl(slug, id), isArtifactDetailPayload);
}

export async function fetchArtifactItemDetail(
  workspaceSlug: string,
  itemId: string
): Promise<ArtifactItemDetailPayload> {
  const slug = workspaceSlug.trim();
  const id = itemId.trim();
  if (!slug || !id) {
    throw new KryspaceRenderServiceError('Workspace slug and item id are required', 'NOT_CONFIGURED');
  }
  return fetchPayload(artifactItemDetailUrl(slug, id), isArtifactItemDetailPayload);
}
